import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { selectAdminToken } from '../../../admin/sessionSlice';
import { setBackButtonUrl } from '../../uiSlice';
import { addReportToServer } from '../../../functions/sendReport';
import { isVotingOpen } from '../../../utils/yogaTheme';
import { themeButtonBackground, themeButtonTextColor } from '../../../utils/colorUtils';
import { apiUrl } from '../../../utils/apiUrl';
import SocialFeed from '../shared/SocialFeed';
import LikePopup from '../shared/LikePopup';
import RulesModal from '../shared/RulesModal';
import CategorySlider from '../shared/CategorySlider';
import {
  ALL_FILTER,
  EMAIL_RE,
  MESSAGE_MAX_LEN,
  apiPost,
  buildCategories,
  formatGameSubtitle,
  isTrue,
  PENDING_APPROVAL_MESSAGE,
} from '../../../utils/gameHelpers';
import './uploadPuzzleGame.css';

export default function UploadPuzzleGame({ activeGame }) {
  const dispatch = useDispatch();
  const { data: themeData } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  // buildCategories already excludes "ALL"
  const categories = useMemo(() => buildCategories(themeData), [themeData]);

  const textColor = themeData?.text_color || '#000000';
  const likeBoxText = themeData?.like_box_text_color || '#ffffff';
  const likeBoxBg = themeData?.like_box_background_color || '#e25569';
  const btnBg = themeButtonBackground(themeData) || '#088e01';
  const btnText = themeButtonTextColor(themeData) || '#ffffff';
  const votingOpen = isVotingOpen(themeData);
  const uploadOn = isTrue(themeData?.badges_share_status ?? 'true');
  const videoUploadOn = isTrue(themeData?.upload_images ?? 'true');
  const fileAccept = videoUploadOn ? 'image/*,video/mp4' : 'image/*';
  const rules = useMemo(
    () => (Array.isArray(themeData?.rules) ? themeData.rules : []),
    [themeData]
  );

  const labels = {
    name: themeData?.lable_name_text || 'Your Name',
    colleagueName: themeData?.lable_colleague_name_text || 'Colleague Name',
    colleagueEmail: themeData?.lable_colleague_email_text || 'Colleague Email',
    badge: themeData?.lable_badge_text || 'Select Category',
  };

  const [name, setName] = useState('');
  const [colleagueName, setColleagueName] = useState('');
  const [colleagueEmail, setColleagueEmail] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [likingId, setLikingId] = useState(null);
  const [likePopupMode, setLikePopupMode] = useState(null);
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER);
  const [page, setPage] = useState(1);
  const [rulesOpen, setRulesOpen] = useState(false);
  const reportIdRef = useRef(null);

  const selectedCategory = categories[selectedIndex] || categories[0];

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || '/login?&save=true'));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    if (user?.name) setName((prev) => prev || user.name);
  }, [user?.name]);

  useEffect(() => {
    if (selectedIndex >= categories.length) setSelectedIndex(0);
  }, [categories.length, selectedIndex]);

  // Feed filter still includes ALL; category picker does not
  const filterOptions = useMemo(
    () => [ALL_FILTER, ...categories.map((c) => c.name)],
    [categories]
  );

  const filteredPosts = useMemo(() => {
    if (activeFilter === ALL_FILTER) return posts;
    return posts.filter((p) => p.category === activeFilter);
  }, [posts, activeFilter]);

  const loadPosts = useCallback(async () => {
    if (!adminToken) {
      setLoadingPosts(false);
      return;
    }
    setLoadingPosts(true);
    try {
      const { ok, payload } = await apiPost('/gameGallery', adminToken, {
        userId: user?.userId || '',
        activeGame,
        limit: 100,
      });
      setPosts(ok && payload?.success && Array.isArray(payload.posts) ? payload.posts : []);
    } finally {
      setLoadingPosts(false);
    }
  }, [adminToken, user?.userId, activeGame]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const uploadFile = async (fileToUpload) => {
    const formData = new FormData();
    formData.append('file', fileToUpload);
    formData.append('userId', user.userId);
    formData.append('name', name.trim());

    const resp = await fetch(apiUrl('/uploadGameMedia'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${adminToken}` },
      body: formData,
    });
    const json = await resp.json();
    if (!resp.ok || !json.success) throw new Error(json.error || 'Upload failed');
    const files = json.files || (json.filename ? [json.filename] : []);
    return files[0] || json.filename || '';
  };

  const handleSubmit = async () => {
    if (!uploadOn || !user || !adminToken) return;
    if (!name.trim() || !colleagueName.trim() || !colleagueEmail.trim() || !EMAIL_RE.test(colleagueEmail.trim())) {
      Swal.fire('Required fields', 'Please fill all fields with a valid email.', 'warning');
      return;
    }
    if (!selectedCategory?.name) {
      Swal.fire('Required', 'Please select a category.', 'warning');
      return;
    }
    if (!message.trim()) {
      Swal.fire('Required', 'Please write a message.', 'warning');
      return;
    }
    if (message.trim().length > MESSAGE_MAX_LEN) {
      Swal.fire('Too long', `Message must be at most ${MESSAGE_MAX_LEN} characters.`, 'warning');
      return;
    }

    setSubmitting(true);
    try {
      let uploadFilename = '';
      if (file) {
        uploadFilename = await uploadFile(file);
      }

      const { ok, payload } = await apiPost('/gameSubmit', adminToken, {
        userId: user.userId,
        name: name.trim(),
        email: user.email || '',
        colleagueName: colleagueName.trim(),
        colleagueEmail: colleagueEmail.trim(),
        category: selectedCategory?.name || '',
        message: message.trim().slice(0, MESSAGE_MAX_LEN),
        icon: uploadFilename || selectedCategory?.icon || '',
        uploadFile: uploadFilename,
        mediaType: file?.type?.startsWith('video') ? 'video' : 'image',
        activeGame,
        gameId: user.gameId || '',
      });

      if (!ok || !payload?.success) {
        Swal.fire(
          payload?.limitReached ? 'Limit reached' : 'Error',
          payload?.error || 'Submit failed.',
          payload?.limitReached ? 'info' : 'error'
        );
        return;
      }

      if (payload.approval === 'pending') {
        Swal.fire('Submitted', PENDING_APPROVAL_MESSAGE, 'info');
      } else {
        Swal.fire('Success', 'Your post has been shared!', 'success');
      }

      addReportToServer(user, { points: 0, reportTime: '00:00', answers: ['upload'], reportIdRef });
      setFile(null);
      setMessage('');
      setColleagueName('');
      setColleagueEmail('');
      await loadPosts();
    } catch (e) {
      Swal.fire('Error', e.message || 'Submit failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (post) => {
    if (!votingOpen || !user || likingId) return;
    if (post.likedByMe) {
      setLikePopupMode('already');
      return;
    }
    setLikingId(post.id);
    try {
      const { ok, payload } = await apiPost('/yogaLike', adminToken, {
        userId: user.userId,
        gameId: post.id,
      });
      if (ok && payload?.success) {
        if (payload.liked) {
          setPosts((prev) =>
            prev.map((p) =>
              p.id === post.id
                ? { ...p, likedByMe: true, likeCount: payload.likeCount ?? (p.likeCount || 0) + 1 }
                : p
            )
          );
          setLikePopupMode('thanks');
        } else {
          setPosts((prev) =>
            prev.map((p) => (p.id === post.id ? { ...p, likedByMe: true } : p))
          );
          setLikePopupMode('already');
        }
      }
    } finally {
      setLikingId(null);
    }
  };

  const handleDelete = async (postId) => {
    if (!user?.userId || !adminToken) {
      Swal.fire('Error', 'You must be logged in to delete a post.', 'error');
      return;
    }
    const confirm = await Swal.fire({
      title: 'Delete this card?',
      text: 'Only you can delete your own posts.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      confirmButtonColor: '#e23b3b',
    });
    if (!confirm.isConfirmed) return;
    const { ok, payload } = await apiPost('/gameDelete', adminToken, {
      userId: user.userId,
      gameId: postId,
    });
    if (ok && payload?.success) {
      await loadPosts();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1400, showConfirmButton: false });
      return;
    }
    Swal.fire('Error', payload?.error || 'Could not delete this post.', 'error');
  };

  return (
    <div className="upload-puzzle-game cg-page" style={{ color: textColor }}>
      <button
        type="button"
        className="upload-rules-top-btn"
        style={{ color: textColor, borderColor: textColor }}
        onClick={() => setRulesOpen(true)}
      >
        Rules
      </button>

      <h1 style={{ color: textColor }}>{themeData?.game_page_title || themeData?.themeName}</h1>
      <p className="upload-subtitle" style={{ color: textColor }}>
        {formatGameSubtitle(themeData?.game_page_sub_title)}
      </p>

      <CategorySlider items={categories} showLabels ariaLabel="Categories" />

      {uploadOn && (
        <div className="upload-form" style={{ borderColor: textColor }}>
          <div className="upload-field">
            <label style={{ color: textColor }}>{labels.name} *</label>
            <input type="text" value={name} readOnly />
          </div>
          <div className="upload-field">
            <label style={{ color: textColor }}>{labels.colleagueName} *</label>
            <input value={colleagueName} onChange={(e) => setColleagueName(e.target.value)} />
          </div>
          <div className="upload-field">
            <label style={{ color: textColor }}>{labels.colleagueEmail} *</label>
            <input type="email" value={colleagueEmail} onChange={(e) => setColleagueEmail(e.target.value)} />
          </div>
          <div className="upload-field">
            <label style={{ color: textColor }}>{labels.badge} *</label>
            <select value={selectedIndex} onChange={(e) => setSelectedIndex(parseInt(e.target.value, 10))}>
              {categories.map((c, i) => (
                <option key={c.key} value={i}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="upload-field">
            <label style={{ color: textColor }}>{videoUploadOn ? 'Photo / Video (MP4 max 50 MB)' : 'Photo'}</label>
            <input
              type="file"
              accept={fileAccept}
              onChange={(e) => {
                const next = e.target.files?.[0] || null;
                if (next?.type?.startsWith('video') && next.size > 50 * 1024 * 1024) {
                  Swal.fire('File too large', 'Video must be MP4 and at most 50 MB.', 'warning');
                  e.target.value = '';
                  setFile(null);
                  return;
                }
                setFile(next);
              }}
            />
          </div>
          <div className="upload-field">
            <label style={{ color: textColor }}>Your message:- *</label>
            <textarea
              value={message}
              maxLength={MESSAGE_MAX_LEN}
              onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX_LEN))}
              rows={3}
              required
            />
            <div className="upload-charcount" style={{ color: textColor }}>
              {message.length}/{MESSAGE_MAX_LEN} Characters
            </div>
          </div>
          <button
            type="button"
            className="upload-submit"
            style={{ backgroundColor: btnBg, color: btnText, borderColor: btnBg }}
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Uploading…' : 'Submit'}
          </button>
        </div>
      )}

      <SocialFeed
        posts={filteredPosts}
        loading={loadingPosts}
        page={page}
        onPageChange={setPage}
        activeFilter={activeFilter}
        onFilterChange={(f) => {
          setActiveFilter(f);
          setPage(1);
        }}
        filterOptions={filterOptions}
        votingOpen={votingOpen}
        likingId={likingId}
        onLike={handleLike}
        onDelete={handleDelete}
        likeBoxBg={likeBoxBg}
        likeBoxText={likeBoxText}
        textColor={textColor}
      />

      <LikePopup mode={likePopupMode} onClose={() => setLikePopupMode(null)} />

      <RulesModal
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        rules={rules}
        btnBg={btnBg}
        btnText={btnText}
      />
    </div>
  );
}
